import { Combobox } from "@foldkit/ui";
import { Effect, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import { defineMessageUnion } from "foldkit/message";
import { defineView } from "foldkit/submodel";
import * as Update from "foldkit/update";

import { apiData, settle, start } from "../api/asyncData";
import { getSearch } from "../api/commands/records";
import { SearchResult } from "../api/schemas/records";
import { i18next } from "../i18n";
import type { Api, Shared } from "../pages/shared";
import * as Icons from "./icons";

/**
 * Record picker: debounced combobox over records, one chosen record,
 * and a host-settable validation flag. Shared by the keep-in-touch swap
 * and Home capture. Now-only: the search never carries `asOf`.
 */

export const Chosen = S.Struct({ id: S.String, name: S.String });
export type Chosen = typeof Chosen.Type;

export const Model = S.Struct({
	combobox: Combobox.Model,
	revision: S.Number,
	query: S.String,
	results: apiData(S.Array(SearchResult)).schema,
	chosen: S.Option(Chosen),
	/** Set by the host via `requireChoice` when a submit needs a record; cleared by any choice. */
	invalid: S.Boolean,
});
export type Model = typeof Model.Type;

export const init: Model = {
	combobox: Combobox.init({ id: "record-picker-combobox" }),
	revision: 0,
	query: "",
	results: AsyncData.Idle(),
	chosen: Option.none(),
	invalid: false,
};

export const Message = defineMessageUnion({
	GotComboboxMessage: { message: Combobox.Message },
	SearchDebounced: { revision: S.Number },
	ChangedQuery: { value: S.String },
	Submitted: {},
	CompletedSearch: {
		search: S.String,
		cursor: S.Number,
		searchType: S.NullOr(S.String),
		knotType: S.NullOr(S.String),
		tags: S.Array(S.String),
		generation: S.Number,
		result: getSearch.Result,
	},
	Chose: Chosen.fields,
	Cleared: {},
});
export type Message = typeof Message.Type;

export const Search = getSearch.command(Message.CompletedSearch);
export const Debounce = Command.define("DebounceRecordPicker", {
	args: { revision: S.Number },
	messages: [Message.SearchDebounced],
	execute: ({ revision }) =>
		Effect.sleep("250 millis").pipe(
			Effect.as(Message.SearchDebounced({ revision })),
		),
});
const bundle = Combobox.create<string>();

type Return = Update.Return<Model, Message, Api>;

/** Host preselect (Reach out): sets the choice with no search and clears the flag. */
export const choose = (model: Model, record: Chosen): Model => ({
	...model,
	combobox: bundle.selectItem(model.combobox, record.id, record.name).model,
	query: "",
	revision: model.revision + 1,
	chosen: Option.some(record),
	results: AsyncData.Idle(),
	invalid: false,
});

/** Host validation (R9): a submit with nothing chosen. */
export const requireChoice = (model: Model): Model => ({
	...model,
	invalid: true,
});

const foldCombobox = Update.foldChild({
	update: bundle.update,
	read: (model: Model) => Option.some(model.combobox),
	write: (model: Model, combobox: Combobox.Model) => ({ ...model, combobox }),
	toParentMessage: (message: Combobox.Message) =>
		Message.GotComboboxMessage({ message }),
	foldOutMessage:
		(out: Combobox.OutMessage) =>
		(model: Model): Return =>
			Combobox.OutMessage.match(out, {
				Selected: ({ value }) => {
					const row = AsyncData.getOrElse(model.results, () => []).find(
						(row) => row.id === value,
					);
					return {
						model: row ? choose(model, { id: row.id, name: row.title }) : model,
					};
				},
				ClearedSelection: () => ({
					model: { ...model, chosen: Option.none() },
				}),
			}),
});

export const update = (
	model: Model,
	message: Message,
	shared: Shared,
): Return =>
	Message.match<Return>(message, {
		GotComboboxMessage: ({ message }) => {
			const folded = foldCombobox(model, message);
			if (message._tag !== "UpdatedInputValue") return folded;
			const changed = update(
				folded.model,
				Message.ChangedQuery({ value: message.value }),
				shared,
			);
			return {
				...changed,
				commands: [...(folded.commands ?? []), ...(changed.commands ?? [])],
			};
		},
		ChangedQuery: ({ value }) => {
			const revision = model.revision + 1;
			return {
				model: {
					...model,
					query: value,
					chosen: Option.none(),
					revision,
					results: value.trim() ? AsyncData.Loading() : AsyncData.Idle(),
				},
				commands: value.trim() ? [Debounce({ revision })] : [],
			};
		},
		SearchDebounced: ({ revision }) =>
			revision !== model.revision
				? { model }
				: update(model, Message.Submitted(), shared),
		Submitted: () =>
			model.query.trim() === ""
				? { model }
				: {
						model: { ...model, results: start(model.results) },
						commands: [
							Search({
								search: model.query.trim(),
								cursor: 0,
								searchType: "node",
								knotType: null,
								tags: [],
								generation: shared.generation,
							}),
						],
					},
		CompletedSearch: ({ search, generation, result }) =>
			generation !== shared.generation || search !== model.query.trim()
				? { model }
				: {
						model: {
							...model,
							results: settle(
								model.results,
								Result.map(result, (page) =>
									page.data.filter((row) => row.type === "node"),
								),
							),
						},
					},
		Chose: ({ id, name }) => ({ model: choose(model, { id, name }) }),
		Cleared: () => ({ model: { ...init, revision: model.revision + 1 } }),
	});

const t = (key: string, options?: Record<string, string>) =>
	i18next.t(`recordPicker.${key}`, options ?? {});

export type ViewInputs = Readonly<Record<never, never>>;

export const view = defineView<Model, Message, ViewInputs>((model, _, h) => {
	const results = AsyncData.getOrElse(model.results, () => []);
	const resultById = new Map(results.map((row) => [row.id, row]));
	return h.div(
		[h.Class("relative flex flex-col gap-2")],
		[
			h.span(
				[
					h.Class(
						"pointer-events-none absolute left-3.5 top-3.5 z-10 text-base-content/45",
					),
				],
				[Icons.Search(h, { size: 18 })],
			),
			h.submodel({
				slotId: "record-combobox",
				view: bundle.view,
				model: model.combobox,
				toParentMessage: (message) => Message.GotComboboxMessage({ message }),
				viewInputs: {
					items: results.map((row) => row.id),
					maybeSelectedValue: Option.map(model.chosen, (record) => record.id),
					restingInputValue: Option.getOrElse(
						Option.map(model.chosen, (record) => record.name),
						() => "",
					),
					itemToValue: (id) => id,
					itemToDisplayText: (id) => resultById.get(id)?.title ?? "",
					itemToConfig: (id, { isActive }) => {
						const row = resultById.get(id);
						const title = row?.title ?? "";
						const subtitle = row?.subtitle.trim() ?? "";
						return {
							className: `cursor-pointer rounded-lg px-3 py-2.5 transition-colors ${isActive ? "bg-primary/8 text-base-content" : "hover:bg-base-200/60"}`,
							content: h.div(
								[h.Class("flex min-w-0 items-center gap-3")],
								[
									h.span(
										[
											h.Attribute("aria-hidden", "true"),
											h.Class(
												"flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary",
											),
										],
										[
											title
												.trim()
												.split(/\s+/)
												.slice(0, 2)
												.map((part) => part[0] ?? "")
												.join("")
												.toUpperCase(),
										],
									),
									h.div(
										[h.Class("min-w-0")],
										[
											h.div(
												[h.Class("text-sm font-medium break-words")],
												[title],
											),
											subtitle && subtitle !== title.trim()
												? h.div(
														[
															h.Class(
																"mt-0.5 text-xs text-base-content/55 break-words",
															),
														],
														[subtitle],
													)
												: h.empty,
										],
									),
								],
							),
						};
					},
					ariaLabel: t("searchLabel"),
					inputPlaceholder: t("searchPlaceholder"),
					className: "w-full",
					inputWrapperClassName: "w-full",
					inputClassName: "record-picker-input",
					itemsClassName:
						"record-picker-options z-50 rounded-xl border border-base-300/70 bg-base-100 p-1.5 shadow-lg",
					itemsScrollClassName: "max-h-72 overflow-y-auto overscroll-contain",
					anchor: { gap: 6, padding: 12 },
					openOnFocus: true,
					isInvalid: model.invalid,
				},
			}),
			Option.isNone(model.chosen) && model.combobox.isOpen
				? h.div(
						[
							h.Role("status"),
							h.AriaLive("polite"),
							h.Class("text-sm text-base-content/70"),
						],
						AsyncData.isLoading(model.results)
							? [
									Icons.Loader2(h, {
										size: 16,
										class: "inline animate-spin mr-2",
									}),
									t("loading"),
								]
							: AsyncData.isFailure(model.results)
								? [
										t("failed"),
										h.button(
											[
												h.Type("button"),
												h.Class("btn btn-ghost btn-xs ml-2"),
												h.AriaLabel(t("retry")),
												h.OnClick(Message.Submitted()),
											],
											[t("retry")],
										),
									]
								: model.query.trim() === ""
									? [t("hint")]
									: AsyncData.isSuccess(model.results) && results.length === 0
										? [
												h.span(
													[h.DataAttribute("testid", "record-picker-empty")],
													[t("noResults")],
												),
											]
										: [],
					)
				: h.empty,
			Option.match(model.chosen, {
				onNone: () => h.empty,
				onSome: (chosen) =>
					h.div(
						[h.Class("flex items-center gap-2 text-sm")],
						[
							h.span(
								[
									h.DataAttribute("testid", "record-picker-chosen"),
									h.Class("badge badge-primary"),
								],
								[chosen.name],
							),
							h.button(
								[
									h.Type("button"),
									h.Class("btn btn-xs btn-ghost"),
									h.AriaLabel(t("clearLabel", { name: chosen.name })),
									h.OnClick(Message.Cleared()),
								],
								[t("clear")],
							),
						],
					),
			}),
			h.p(
				[
					h.DataAttribute("testid", "record-picker-message"),
					h.AriaLive("polite"),
					h.Class("text-sm text-error"),
				],
				[model.invalid ? t("required") : ""],
			),
		],
	);
});
